// Role-based permission helper functions

export interface UserRole {
  id: string;
  name: string;
  // Add other role properties as needed
}

export interface AccountUser {
  role: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface UserSettings {
  role: string;
  firstName: string;
  lastName: string;
  email: string;
  accountUsers?: AccountUser[];
}

// Roles that can update mileage deviation status and comments
export const ADMIN_ROLES = ["Super Admin", "Admin", "Manager"];

// Restricted roles that should not access certain pages
export const RESTRICTED_ROLES = ["Driver"];

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

/**
 * Get the actual role ID from userSettings
 * @param userSettings - User settings object from API
 * @param accountUser - Account user object (not used currently, kept for backward compatibility)
 * @returns string - Role ID to use for permission checks
 */
const getRoleIdFromSettings = (
  userSettings: UserSettings | null,
  accountUser?: any
): string | null => {
  if (!userSettings) return null;

  if (userSettings.role && userSettings.role !== EMPTY_GUID) {
    return userSettings.role;
  }

  if (userSettings.accountUsers && userSettings.accountUsers.length > 0) {
    const firstAccountUser = userSettings.accountUsers[0];
    if (firstAccountUser.role && firstAccountUser.role !== EMPTY_GUID) {
      return firstAccountUser.role;
    }
  }

  return null;
};

/**
 * Check if user is a Driver (restricted access)
 * @param roles - Array of user roles
 * @returns boolean - true if user is a Driver
 */
export const isDriver = (roles: UserRole[] = []): boolean => {
  return roles.some((role) => RESTRICTED_ROLES.includes(role.name));
};

/**
 * Check if user is a Driver based on userSettings role uniqueId
 * @param userSettings - User settings object from API
 * @param allRoles - Array of all available roles
 * @param accountUser - Account user object (optional, for more reliable role detection)
 * @returns boolean - true if user is a Driver
 */
export const isDriverBySettings = (
  userSettings: UserSettings | null,
  allRoles: UserRole[] = [],
  accountUser?: any
): boolean => {
  const roleId = getRoleIdFromSettings(userSettings, accountUser);

  if (!roleId) {
    return false;
  }

  const userRole = allRoles.find((role) => role.id === roleId);

  if (!userRole) {
    if (allRoles.length === 0) {
      return false;
    }

    return false;
  }

  const roleName = userRole.name;
  const result = RESTRICTED_ROLES.includes(roleName);

  return result;
};

/**
 * Check if user has admin permissions based on role name
 * @param roles - Array of user roles
 * @returns boolean - true if user has admin permissions
 */
export const hasAdminPermissions = (roles: UserRole[] = []): boolean => {
  return roles.some((role) => ADMIN_ROLES.includes(role.name));
};

/**
 * Check if user has admin permissions based on userSettings role uniqueId
 * @param userSettings - User settings object from API
 * @param allRoles - Array of all available roles
 * @param accountUser - Account user object (optional, for more reliable role detection)
 * @returns boolean - true if user has admin permissions
 */
export const hasAdminPermissionsBySettings = (
  userSettings: UserSettings | null,
  allRoles: UserRole[] = [],
  accountUser?: any
): boolean => {
  const roleId = getRoleIdFromSettings(userSettings, accountUser);

  if (!roleId) {
    return false;
  }

  // Find the role by uniqueId
  const userRole = allRoles.find((role) => role.id === roleId);

  if (!userRole) {
    if (allRoles.length === 0) {
      return false;
    }

    return true;
  }

  const roleName = userRole.name;

  return ADMIN_ROLES.includes(roleName);
};

/**
 * Get user role name from userSettings role uniqueId
 * @param userSettings - User settings object from API
 * @param allRoles - Array of all available roles
 * @param accountUser - Account user object (optional, for more reliable role detection)
 * @returns string - Role name or 'Unknown' if not found
 */
export const getUserRoleNameFromSettings = (
  userSettings: UserSettings | null,
  allRoles: UserRole[] = [],
  accountUser?: any
): string => {
  const roleId = getRoleIdFromSettings(userSettings, accountUser);

  if (!roleId) {
    return "Unknown";
  }

  const userRole = allRoles.find((role) => role.id === roleId);

  if (!userRole) {
    if (allRoles.length === 0) {
      return "Unknown";
    }

    return "Super Admin";
  }

  return userRole.name;
};

/**
 * Check if user can edit mileage deviation status (legacy - using roles array)
 * @param roles - Array of user roles
 * @returns boolean - true if user can edit status
 */
export const canEditMileageDeviationStatus = (
  roles: UserRole[] = []
): boolean => {
  return hasAdminPermissions(roles);
};

/**
 * Check if user can edit mileage deviation status (new - using userSettings)
 * @param userSettings - User settings object from API
 * @param allRoles - Array of all available roles
 * @param accountUser - Account user object (optional, for more reliable role detection)
 * @returns boolean - true if user can edit status
 */
export const canEditMileageDeviationStatusBySettings = (
  userSettings: UserSettings | null,
  allRoles: UserRole[] = [],
  accountUser?: any
): boolean => {
  return hasAdminPermissionsBySettings(userSettings, allRoles, accountUser);
};

/**
 * Check if user can edit mileage deviation comments (legacy - using roles array)
 * @param roles - Array of user roles
 * @returns boolean - true if user can edit comments
 */
export const canEditMileageDeviationComments = (
  roles: UserRole[] = []
): boolean => {
  return hasAdminPermissions(roles);
};

/**
 * Check if user can edit mileage deviation comments (new - using userSettings)
 * @param userSettings - User settings object from API
 * @param allRoles - Array of all available roles
 * @param accountUser - Account user object (optional, for more reliable role detection)
 * @returns boolean - true if user can edit comments
 */
export const canEditMileageDeviationCommentsBySettings = (
  userSettings: UserSettings | null,
  allRoles: UserRole[] = [],
  accountUser?: any
): boolean => {
  return hasAdminPermissionsBySettings(userSettings, allRoles, accountUser);
};

/**
 * Check if user can access Mileage Deviations page (requires admin permissions)
 * @param userSettings - User settings object from API
 * @param allRoles - Array of all available roles
 * @param accountUser - Account user object (optional, for more reliable role detection)
 * @returns boolean - true if user can access Mileage Deviations
 */
export const canAccessMileageDeviations = (
  userSettings: UserSettings | null,
  allRoles: UserRole[] = [],
  accountUser?: any
): boolean => {
  return hasAdminPermissionsBySettings(userSettings, allRoles, accountUser);
};

/**
 * Get user role names as array of strings
 * @param roles - Array of user roles
 * @returns string[] - Array of role names
 */
export const getRoleNames = (roles: UserRole[] = []): string[] => {
  return roles.map((role) => role.name);
};
